/**
 * THE STICKY TOP BAR — everything that is true of the whole screen, whatever
 * tab is open.
 *
 * Scope breadcrumb · date range + compare · granularity · Export · the caption ·
 * the enforcement alert.
 *
 * EXPORT IS THE ONLY ACTION HERE. Density moved out (the rail and the URL still
 * carry it), and "Ask IQ about this view" moved to the floating IQ launcher,
 * which is on every screen rather than only on this one — two doors to the same
 * assistant taught two different habits. The Filters button survives ONLY below
 * `lg`, where `Rail` is `display: none` and it is the only way to reach the
 * filters at all.
 *
 * It reserves `layout.bellDockReserve` on its right: the NotificationDock is
 * `position: fixed` and lands on whatever a top-anchored header puts in that
 * corner. `src/app-surfaces.test.ts` does the arithmetic.
 */
import React, { useCallback, useRef, useState } from 'react';
/* WP-34 — the shared scroll affordance. These chip strips are deliberate nowrap
   rows that hide their scrollbar; nothing said there was more to the right. */
import { ScrollStrip } from '../../../../scroll/scroll-affordance';
import { useMenuDismiss } from '../../../../hooks/use-menu-dismiss';
import { overlayMotion, Presence } from '../../../../../motion';
import { CompareScopePicker } from './compare-scope';
import {
  AnalyticsScope,
  AnalyticsSpan,
  AnalyticsWindow,
} from '../../../../../api/analytics-client';
import {
  CUSTOM_RANGE,
  DashboardFilter,
  FilterMeta,
  Granularity,
} from '../state/filter-model';
import { FilterAction } from '../state/filter-reducer';
import {
  Actions,
  Breadcrumb,
  Button,
  ChipRow,
  CrumbButton,
  CrumbSep,
  Field,
  Input,
  Menu,
  MenuAnchor,
  MenuItem,
  PhoneOnlyButton,
  RangeChip,
  ToggleField,
  ToggleInput,
  TopBar as Bar,
  TopRow,
} from '../dashboard-screen.styled';
import { ScopeCaption } from './scope-caption';
import { ScopeWarning } from './scope-warning';
import { activePresetId, fromDateInput, presetsFor, toDateInput } from './date-presets';
import { Clock, todayIn } from '../../../../../function-utils/building-time';
import { useClock } from '../../../../../context/clock-context';

export interface TopBarProps {
  filter: DashboardFilter;
  meta?: FilterMeta;
  dispatch: (action: FilterAction, opts?: { replace?: boolean }) => void;
  window?: AnalyticsWindow;
  compareWindow?: AnalyticsSpan | null;
  scope?: AnalyticsScope;
  curSpanMs?: number;
  prevSpanMs?: number;
  likeForLike?: boolean;
  /** Analytics-gated controls are hidden without the permission, not disabled —
   *  an inert control is a promise the server refuses. */
  canReadAnalytics: boolean;
  exportDisabled: boolean;
  onExport: (format: 'csv' | 'xlsx' | 'pdf') => void;
  /** Opens the bottom sheet under lg. */
  onOpenFilters: () => void;
  /** The clock the analytics answer is on (its `tz` / `tz_source`), for the
   *  caption. Undefined until an answer has arrived. */
  answerClock?: Clock;
}

/* The export menu grows from the button's corner and shrinks back into it. */
const POPOVER = overlayMotion('popover');
const POPOVER_ORIGIN = { transformOrigin: 'top right' } as const;

const GRANULARITIES: { id: Granularity; label: string }[] = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
];

export const TopBar: React.FC<TopBarProps> = ({
  filter,
  meta,
  dispatch,
  window: answerWindow,
  compareWindow,
  scope,
  curSpanMs,
  prevSpanMs,
  likeForLike,
  canReadAnalytics,
  exportDisabled,
  onExport,
  onOpenFilters,
  answerClock,
}) => {
  const [exportOpen, setExportOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(filter.range === CUSTOM_RANGE);
  const exportRef = useRef<HTMLDivElement>(null);

  /* Outside-click AND Escape, from the shared helper. This had the first and
     not the second, so a keyboard user who opened the export menu could only
     leave it by activating one of its items. */
  const closeExport = useCallback(() => setExportOpen(false), []);

  useMenuDismiss(exportOpen, closeExport, { menu: exportRef });

  /* The two scrollers the fades read. Separate refs: the period strip and the
     granularity strip scroll independently, and one shared ref would make the
     shorter of them claim the longer one's overflow. */
  const periodRef = useRef<HTMLDivElement>(null);
  const granularityRef = useRef<HTMLDivElement>(null);

  const presets = presetsFor(meta?.ranges || []);
  /* "TODAY" FOR THE PRESETS, on the reader's own clock (R-T12): the device's
     for an owner or a manager, the building's for a concierge. Quarter and
     year-to-date end today; the server then cuts the same days on the same
     clock, so the control and the answer agree. */
  const clock = useClock({ buildingId: filter.buildingId });
  const today = todayIn(clock.zone);
  const active = activePresetId(filter.range, filter.from, filter.to, today);

  const pickPreset = useCallback(
    (id: string) => {
      const preset = presets.find(p => p.id === id);
      if (!preset) return;
      if (preset.id === CUSTOM_RANGE) {
        setCustomOpen(true);

        return;
      }
      setCustomOpen(false);
      if (preset.range) {
        dispatch({ type: 'setRange', range: preset.range });

        return;
      }
      const w = preset.window?.(today);
      if (w) dispatch({ type: 'setCustomRange', from: w.from, to: w.to });
    },
    [dispatch, presets, today]
  );

  const buildingName =
    filter.buildingId != null
      ? meta?.buildings.find(b => b.id === filter.buildingId)?.name
      : undefined;
  const portfolioName =
    filter.portfolioId != null
      ? meta?.portfolios.find(p => p.id === filter.portfolioId)?.name
      : undefined;

  const focusLabel = buildingName || portfolioName;

  return (
    <Bar>
      <TopRow>
        <div style={{ minWidth: 0 }}>
          {/* NO SECOND "Dashboard" HERE (WP-11). The shell already renders an
              h1 for this screen — shared by all ten dashboard screens and wired
              to useDocumentTitle — so this printed the word twice, once in the
              bar and once as the page title. The bar's copy is the one that
              goes: the shell's is the accessible heading and the document
              title, and deleting that would cost both. */}
          {/* THE SCOPE BREADCRUMB. Each segment clears everything below it, so
              stepping back up is one press rather than three controls. */}
          <Breadcrumb aria-label="Scope">
            <CrumbButton
              type="button"
              $current={!portfolioName && !buildingName && !filter.unit}
              onClick={() =>
                dispatch({
                  type: 'setScope',
                  scope: { portfolioId: null, buildingId: null, unit: null },
                })
              }
            >
              Organisation
            </CrumbButton>
            {portfolioName ? (
              <>
                <CrumbSep aria-hidden="true">›</CrumbSep>
                <CrumbButton type="button" $current onClick={() => undefined}>
                  {portfolioName}
                </CrumbButton>
              </>
            ) : null}
            {buildingName ? (
              <>
                <CrumbSep aria-hidden="true">›</CrumbSep>
                <CrumbButton
                  type="button"
                  $current={!filter.unit}
                  onClick={() => dispatch({ type: 'setScope', scope: { unit: null } })}
                >
                  {buildingName}
                </CrumbButton>
              </>
            ) : null}
            {filter.unit ? (
              <>
                <CrumbSep aria-hidden="true">›</CrumbSep>
                <CrumbButton type="button" $current>
                  Unit {filter.unit}
                </CrumbButton>
              </>
            ) : null}
          </Breadcrumb>
        </div>

        <Actions>
          <PhoneOnlyButton type="button" onClick={onOpenFilters} data-testid="open-filters">
            Filters
          </PhoneOnlyButton>

          <MenuAnchor ref={exportRef}>
            <Button
              type="button"
              disabled={exportDisabled}
              aria-haspopup="menu"
              aria-expanded={exportOpen}
              onClick={() => setExportOpen(open => !open)}
              data-testid="export-button"
            >
              Export
            </Button>
            <Presence>
              {exportOpen ? (
                <Menu
                  key="export-menu"
                  {...POPOVER}
                  style={POPOVER_ORIGIN}
                  role="menu"
                  aria-label="Export this view"
                  data-testid="export-menu"
                >
                  {(['csv', 'xlsx', 'pdf'] as const).map(fmt => (
                    <MenuItem
                      key={fmt}
                      type="button"
                      role="menuitem"
                      data-testid={`export-${fmt}`}
                      onClick={() => {
                        setExportOpen(false);
                        onExport(fmt);
                      }}
                    >
                      {fmt.toUpperCase()}
                    </MenuItem>
                  ))}
                </Menu>
              ) : null}
            </Presence>
          </MenuAnchor>
        </Actions>
      </TopRow>

      <TopRow>
        <ScrollStrip scroller={periodRef}>
        <ChipRow role="group" aria-label="Period" ref={periodRef}>
          {presets.map(preset => (
            <RangeChip
              key={preset.id}
              type="button"
              active={active === preset.id}
              aria-pressed={active === preset.id}
              onClick={() => pickPreset(preset.id)}
              data-testid={`range-${preset.id}`}
            >
              {preset.label}
            </RangeChip>
          ))}
        </ChipRow>
        </ScrollStrip>

        <Actions>
          <ScrollStrip scroller={granularityRef}>
          <ChipRow role="group" aria-label="Granularity" ref={granularityRef}>
            {GRANULARITIES.map(g => (
              <RangeChip
                key={g.id}
                type="button"
                active={filter.granularity === g.id}
                aria-pressed={filter.granularity === g.id}
                onClick={() => dispatch({ type: 'setGranularity', granularity: g.id })}
                data-testid={`granularity-${g.id}`}
              >
                {g.label}
              </RangeChip>
            ))}
          </ChipRow>
          </ScrollStrip>

          {/* THE TWO COMPARISONS, SIDE BY SIDE AND MUTUALLY EXCLUSIVE.
              Ticking either clears the other — the rule lives in the reducer so
              a pasted link carrying both is corrected too. They sit next to each
              other rather than in one three-way control because they are not
              alternatives to each other for most readers: one is about time and
              one is about place. */}
          {canReadAnalytics && (meta?.compare || []).includes('previous_period') ? (
            <ToggleField>
              <ToggleInput
                type="checkbox"
                checked={filter.compare === 'previous_period'}
                onChange={e =>
                  dispatch({
                    type: 'setCompare',
                    compare: e.target.checked ? 'previous_period' : 'none',
                  })
                }
                data-testid="compare-toggle"
              />
              vs previous period
            </ToggleField>
          ) : null}

          {canReadAnalytics ? (
            <CompareScopePicker filter={filter} meta={meta} dispatch={dispatch} />
          ) : null}
        </Actions>
      </TopRow>

      {customOpen ? (
        <TopRow>
          <Field>
            <span>From</span>
            <Input
              type="date"
              value={toDateInput(filter.from)}
              max={toDateInput(filter.to) || undefined}
              data-testid="custom-from"
              onChange={e => {
                // THE DAY PICKED IS THE DAY ASKED FOR. The input's value is a
                // day key and it is stored and sent as one; nothing turns it
                // into an instant on this device's clock.
                const from = fromDateInput(e.target.value);
                const to = filter.to && from && filter.to >= from ? filter.to : from;
                if (from && to) dispatch({ type: 'setCustomRange', from, to });
              }}
            />
          </Field>
          <Field>
            <span>To</span>
            <Input
              type="date"
              // The INCLUSIVE last day — which is also exactly what is stored.
              value={toDateInput(filter.to)}
              min={toDateInput(filter.from) || undefined}
              data-testid="custom-to"
              onChange={e => {
                const to = fromDateInput(e.target.value);
                const from = filter.from && to && filter.from <= to ? filter.from : to;
                if (from && to) dispatch({ type: 'setCustomRange', from, to });
              }}
            />
          </Field>
        </TopRow>
      ) : null}

      {canReadAnalytics ? (
        <ScopeCaption
          window={answerWindow}
          compareWindow={compareWindow}
          scope={scope}
          curSpanMs={curSpanMs}
          prevSpanMs={prevSpanMs}
          likeForLike={likeForLike}
          focusLabel={focusLabel}
          clock={answerClock}
        />
      ) : null}

      <ScopeWarning scope={scope} />
    </Bar>
  );
};

